import { motion } from "framer-motion";
import { Phone } from "lucide-react";
import { canDialFromDevice, stripTel } from "@/lib/tel";
import { trackCallClick } from "@/lib/ga4";

interface GenieCallButtonProps {
  phoneNumber?: string | null;
  brandColor: string;
  secondaryColor?: string | null;
  agentName?: string | null;
}

/**
 * Header pill that nudges phone visitors onto a real call with the Genie's
 * inbound number instead of web voice. Renders only on a handset (where `tel:`
 * opens the dialer) and only when the Genie has a number.
 */
export const GenieCallButton = ({ phoneNumber, brandColor, secondaryColor, agentName }: GenieCallButtonProps) => {
  if (!canDialFromDevice(phoneNumber)) return null;

  return (
    <motion.a
      href={`tel:${stripTel(phoneNumber)}`}
      onClick={() => trackCallClick("header_call_button")}
      aria-label={`Call ${agentName || "this Genie"} at ${phoneNumber}`}
      initial={{ opacity: 0, scale: 0.85 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      className="relative inline-flex min-h-[44px] shrink-0 items-center gap-2 rounded-full px-4 text-sm font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-genie-primary focus-visible:ring-offset-2"
      style={{
        background: `linear-gradient(135deg, ${brandColor}, ${secondaryColor || brandColor})`,
        boxShadow: `0 0 18px ${brandColor}55, 0 4px 14px rgba(15,23,42,0.12)`,
      }}
    >
      <span className="relative flex h-4 w-4 items-center justify-center" aria-hidden="true">
        <span className="absolute inset-0 rounded-full bg-white/40 animate-ping motion-reduce:hidden" />
        <Phone size={16} strokeWidth={2.25} className="relative" />
      </span>
      Call
    </motion.a>
  );
};

export default GenieCallButton;
